/**
 * PATH: packages/ui/src/kanban/dateParse.ts
 * FILE_ID: UI-KANBAN-DATE-PARSE-001
 * PURPOSE: Pure helpers that turn what an operator TYPES into a card date
 *          field ("tomorrow", "next fri", "25/12", "15 jun") into a
 *          canonical ISO date string, plus the small date-math the
 *          DateField calendar grid needs.
 * WHY:
 *   Operator ask (FR card_composer_date_autofill, 2026-06-15):
 *   "When I am writing a date here on the card I should have a drop down
 *   selection with calendar autofill." A native <input type="date"> only
 *   accepts the browser's segmented widget; you cannot type "next friday"
 *   or "25/12". This module is the typed-text to ISO brain behind the
 *   new DateField so the parsing is testable in isolation from React.
 *
 * INPUT -> OUTPUT
 *   parseDateInput("tomorrow", today) -> "2026-06-16"
 *   parseDateInput("25/12", today)    -> "2026-12-25"  (day-first, AU)
 *   parseDateInput("garbage", today)  -> null
 *
 * No external deps (no date-fns / react-day-picker): this lives in the
 * shared @quotech/ui kanban bundle which every hub imports, so it stays
 * dependency-free. All math is local-time (calendar) date math; there is
 * no timezone conversion because card dates are calendar days, not
 * instants. Week-N starts reuse `startOfWeekMonday` from localDate so
 * typed "week 6" cannot drift from Time view / Smart Time.
 */

import { startOfWeekMonday } from './utils/localDate'

/** Zero-pad to 2 digits. */
function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Canonical ISO calendar date (YYYY-MM-DD) for a Date, read in LOCAL
 * time. We never use toISOString() because that shifts to UTC and can
 * roll the calendar day backwards/forwards for non-UTC operators.
 */
export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** Local midnight for a Date (drops any time component). */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** A new Date n days after (n may be negative) the given day. */
export function addDays(d: Date, n: number): Date {
  const next = startOfDay(d)
  next.setDate(next.getDate() + n)
  return next
}

/** A new Date n months after (clamped to the month's last day). */
export function addMonths(d: Date, n: number): Date {
  const base = startOfDay(d)
  const targetMonth = base.getMonth() + n
  const candidate = new Date(base.getFullYear(), targetMonth, 1)
  const lastDay = new Date(
    candidate.getFullYear(),
    candidate.getMonth() + 1,
    0,
  ).getDate()
  candidate.setDate(Math.min(base.getDate(), lastDay))
  return candidate
}

/** Parse an ISO calendar date (YYYY-MM-DD) to a local Date, or null. */
export function isoToDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(iso.trim())
  if (!m) return null
  return makeValidDate(Number(m[1]), Number(m[2]), Number(m[3]))
}

/**
 * Build a local Date only if (y, m, d) is a REAL calendar day. Rejects
 * 2026-02-31 etc. by round-tripping the components back out of the Date.
 */
function makeValidDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const d = new Date(year, month - 1, day)
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null
  }
  return d
}

/** Expand a 2-digit year to 20xx; pass 4-digit years through. */
function normaliseYear(raw: number): number {
  if (raw < 100) return 2000 + raw
  return raw
}

const MONTHS: Record<string, number> = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
}

// 0 = Sunday .. 6 = Saturday (matches Date.getDay()).
const WEEKDAYS: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
}

/** Defensive upper bound for "week N" input so a fat-fingered "week 999"
 *  can't schedule a card ~19 years out. 104 weeks = two years — beyond any
 *  realistic programme, same clamp the Time view's typed-week allocate uses. */
export const MAX_RELATIVE_WEEK = 104

/** Start of calendar week N (Mon–Sun). Week 1 is Monday of the week
 *  containing `base`; week N is that Monday + (N-1)*7. Matches Time-view
 *  Week-N bucket `from` dates (gantt-bucket/buckets.buildBuckets) and
 *  Smart Time columns so typed "week 6" and a drop on "Week 6" resolve
 *  to the same Monday. Week 1 typed/dropped mid-week still uses TODAY
 *  on the Time-view This-week lane (no back-date); this helper returns
 *  the Monday so Smart Time can draw the full calendar week. */
export function relativeWeekStart(base: Date, week: number): Date {
  return addDays(startOfWeekMonday(base), (week - 1) * 7)
}

/**
 * Next date (>= base) whose weekday is `weekday`. When includeToday is
 * true and base already is that weekday, returns base.
 */
function nextWeekday(base: Date, weekday: number, includeToday: boolean): Date {
  const start = startOfDay(base)
  let delta = (weekday - start.getDay() + 7) % 7
  if (delta === 0 && !includeToday) delta = 7
  return addDays(start, delta)
}

/**
 * Turn free-typed text into an ISO date (YYYY-MM-DD), or null when it is
 * not a date. `today` is injectable so the relative words are testable.
 *
 * Supported shapes (case-insensitive):
 *   - today | tod | now
 *   - tomorrow | tmrw | tmr | tom
 *   - yesterday | yest
 *   - next week | next month | end of month | eom
 *   - in 3 days | 3 days | in 2 weeks | +2w | +10d | in 1 month
 *   - week 6 | wk 6 | w6                (ARC-6: start of relative week N)
 *   - [this|next] <weekday>            (next friday, mon, this wed)
 *   - 2026-12-25                       (ISO)
 *   - 25/12, 25/12/26, 25-12-2026, 25.12   (day-first, AU)
 *   - 15 jun | 15 june 2026 | jun 15 | december 1
 */
export function parseDateInput(
  text: string,
  today: Date = new Date(),
): string | null {
  if (text == null) return null
  const raw = text.trim().toLowerCase()
  if (raw === '') return null
  const base = startOfDay(today)

  // 1) Relative keywords.
  if (raw === 'today' || raw === 'tod' || raw === 'now') return toISODate(base)
  if (raw === 'tomorrow' || raw === 'tmrw' || raw === 'tmr' || raw === 'tom') {
    return toISODate(addDays(base, 1))
  }
  if (raw === 'yesterday' || raw === 'yest') return toISODate(addDays(base, -1))
  if (raw === 'next week') return toISODate(addDays(base, 7))
  if (raw === 'next month') return toISODate(addMonths(base, 1))
  if (raw === 'end of month' || raw === 'eom') {
    const eom = new Date(base.getFullYear(), base.getMonth() + 1, 0)
    return toISODate(eom)
  }

  // 2) Relative offsets: "in 3 days", "3 days", "+2w", "in 1 month".
  const offset = /^(?:in\s+|\+)?(\d+)\s*(d|day|days|w|wk|wks|week|weeks|m|mo|month|months)$/.exec(raw)
  if (offset) {
    const n = Number(offset[1])
    const unit = offset[2]
    if (unit.startsWith('d')) return toISODate(addDays(base, n))
    if (unit.startsWith('w')) return toISODate(addDays(base, n * 7))
    return toISODate(addMonths(base, n))
  }

  // 2b) ARC-6 (U05): "week 6" / "wk 6" / "w6" — the start of RELATIVE week N.
  // Week 1 = this week (today); week N = today + (N-1)*7 — IDENTICAL to the
  // Time view's week-bucket boundaries (gantt-bucket/buckets.buildBuckets), so
  // a typed "week 6" and a drop on the "Week 6" lane land the card on the SAME
  // date and the two paths can never drift.
  // OPERATOR-QUESTION(ARC-6 MR1): if "week 6" should instead mean calendar ISO
  // week 6 or financial-year week 6, only this block changes — flagged in the
  // MR description; relative semantics chosen to match the existing Week-N
  // lane language everywhere else in kanban.
  const weekN = /^(?:week|wk|w)\s*(\d{1,3})$/.exec(raw)
  if (weekN) {
    const n = Number(weekN[1])
    if (n >= 1 && n <= MAX_RELATIVE_WEEK) {
      return toISODate(relativeWeekStart(base, n))
    }
    return null
  }

  // 3) Weekday words, optionally prefixed with this/next.
  const wd = /^(this|next|coming)?\s*([a-z]+)$/.exec(raw)
  if (wd && WEEKDAYS[wd[2]] !== undefined) {
    const weekday = WEEKDAYS[wd[2]]
    const prefix = wd[1]
    if (prefix === 'next') {
      // Friday of NEXT week: take the soonest future occurrence then,
      // if it is still within this week, push a full week.
      const soonest = nextWeekday(base, weekday, false)
      const deltaDays = Math.round(
        (soonest.getTime() - base.getTime()) / 86400000,
      )
      return toISODate(deltaDays < 7 ? addDays(soonest, 7) : soonest)
    }
    // bare / "this" / "coming": soonest occurrence, today counts.
    return toISODate(nextWeekday(base, weekday, true))
  }

  // 4) ISO date.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw)
  if (iso) {
    const d = makeValidDate(Number(iso[1]), Number(iso[2]), Number(iso[3]))
    return d ? toISODate(d) : null
  }

  // 5) Numeric day-first: 25/12, 25/12/26, 25-12-2026, 25.12 .
  const dmy = /^(\d{1,2})[/.\-](\d{1,2})(?:[/.\-](\d{2,4}))?$/.exec(raw)
  if (dmy) {
    const day = Number(dmy[1])
    const month = Number(dmy[2])
    const year = dmy[3] ? normaliseYear(Number(dmy[3])) : base.getFullYear()
    const d = makeValidDate(year, month, day)
    return d ? toISODate(d) : null
  }

  // 6) Month-name forms: "15 jun", "jun 15", "15 june 2026", "december 1".
  const tokens = raw.split(/[\s,]+/).filter(Boolean)
  if (tokens.length >= 2 && tokens.length <= 3) {
    let month: number | null = null
    let day: number | null = null
    let year: number | null = null
    for (const tok of tokens) {
      if (MONTHS[tok] !== undefined) {
        month = MONTHS[tok]
      } else if (/^\d{1,2}$/.test(tok) && day === null) {
        day = Number(tok)
      } else if (/^\d{4}$/.test(tok)) {
        year = Number(tok)
      } else if (/^\d{1,2}$/.test(tok)) {
        // second small number after a day was already taken -> 2-digit year
        year = normaliseYear(Number(tok))
      } else {
        return null
      }
    }
    if (month !== null && day !== null) {
      const d = makeValidDate(year ?? base.getFullYear(), month, day)
      return d ? toISODate(d) : null
    }
  }

  return null
}

const HUMAN_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HUMAN_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/**
 * Friendly label for an ISO date, e.g. "Fri 19 Jun 2026". Returns '' for
 * empty/invalid input so callers can render a placeholder.
 */
export function formatHumanDate(iso: string | null | undefined): string {
  const d = isoToDate(iso)
  if (!d) return ''
  return `${HUMAN_WEEKDAYS[d.getDay()]} ${d.getDate()} ${HUMAN_MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

/** Short relative hint for presets, e.g. today/tomorrow/in N days. */
export function relativeDateHint(
  iso: string | null | undefined,
  today: Date = new Date(),
): string {
  const d = isoToDate(iso)
  if (!d) return ''
  const days = Math.round((d.getTime() - startOfDay(today).getTime()) / 86400000)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  if (days > 1 && days <= 31) return `in ${days} days`
  if (days < -1 && days >= -31) return `${Math.abs(days)} days ago`
  return ''
}

export interface CalendarCell {
  iso: string
  day: number
  inMonth: boolean
  isToday: boolean
}

/**
 * 6-row x 7-col matrix of calendar cells for the month containing
 * `monthAnchor`, padded with leading/trailing days so each row is a full
 * week. Weeks start on Monday (AU convention). Pure: same inputs always
 * yield the same grid.
 */
export function monthGrid(
  monthAnchor: Date,
  today: Date = new Date(),
): CalendarCell[][] {
  const todayIso = toISODate(startOfDay(today))
  const firstOfMonth = new Date(
    monthAnchor.getFullYear(),
    monthAnchor.getMonth(),
    1,
  )
  // Monday-start offset: getDay() 0=Sun..6=Sat -> Mon=0..Sun=6.
  const leadOffset = (firstOfMonth.getDay() + 6) % 7
  const gridStart = addDays(firstOfMonth, -leadOffset)

  const rows: CalendarCell[][] = []
  for (let week = 0; week < 6; week++) {
    const row: CalendarCell[] = []
    for (let dow = 0; dow < 7; dow++) {
      const cellDate = addDays(gridStart, week * 7 + dow)
      const iso = toISODate(cellDate)
      row.push({
        iso,
        day: cellDate.getDate(),
        inMonth: cellDate.getMonth() === monthAnchor.getMonth(),
        isToday: iso === todayIso,
      })
    }
    rows.push(row)
  }
  return rows
}

export const WEEKDAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export function monthTitle(monthAnchor: Date): string {
  return `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][monthAnchor.getMonth()]} ${monthAnchor.getFullYear()}`
}
